import { useState, useRef, useEffect } from 'react';
import { getFeatureByQId } from '@services/youtube-platform.service';
import { logToCloudWatch } from '@services/logger.service';

const POLL_INTERVAL_MS = 5000;
const BACK_TO_TOP_THRESHOLD_PX = 240;

/**
 * Encapsulates the "previous comments" history for Transient Thoughts:
 * polls the DB-backed feature endpoint while the interaction is live, and
 * exposes scroll helpers for the inline history feed.
 *
 * @param {object}  params
 * @param {string}  params.featureId  RoS feature id (currentRosFeature.id)
 * @param {string}  params.streamId
 * @param {string}  params.ttId       active engagement id; '' when not running
 * @param {boolean} params.enabled    only poll for RoS interactions
 */
export default function useTransientThoughtHistory({ featureId, streamId, ttId, enabled }) {
  const [prevComments, setPrevComments] = useState([]);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const scrollRef = useRef(null);

  const fetchPrevComments = async () => {
    try {
      const response = await getFeatureByQId(featureId, streamId, 'transientThought');
      const fetched = response?.entity?.[0]?.comments || [];
      // Skip the state update (and the whole feed re-render) when nothing changed.
      setPrevComments(prev => (prev.length === fetched.length ? prev : fetched));
    } catch (err) {
      logToCloudWatch('Failed to fetch previous transient thoughts', { streamId, ttId, err }, 'ERROR');
    }
  };

  const handleScroll = (e) => {
    setShowBackToTop(e.target.scrollTop > BACK_TO_TOP_THRESHOLD_PX);
  };

  const scrollToTop = () => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Poll the history while the interaction is live so the section below the
  // live area fills in as comments arrive — making the page scrollable.
  useEffect(() => {
    setPrevComments([]);
    setShowBackToTop(false);
    if (!ttId || !enabled) return undefined;
    fetchPrevComments();
    const intervalId = setInterval(fetchPrevComments, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [ttId, enabled]);

  return {
    prevComments, showBackToTop, scrollRef, handleScroll, scrollToTop
  };
}
